import { useState } from "react";
import BillingSidebar from "@/components/layout/BillingSidebar";
import BillingHeader from "@/components/layout/BillingHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  HelpCircle,
  Book,
  MessageCircle,
  Phone,
  Mail,
  FileText,
  Video,
  Search,
  ExternalLink,
  CheckCircle,
} from "lucide-react";
import { toast } from "sonner";

const faqs = [
  {
    question: "How do I submit a claim?",
    answer: "Navigate to the Claims page, select the pending claims you want to submit, and click the 'Submit' button. You can also submit claims individually by clicking the submit icon on each row.",
  },
  {
    question: "What do the denial codes mean?",
    answer: "Denial codes follow ANSI standards. CO codes indicate contractual obligations, PR codes indicate patient responsibility. Visit the Denials page to see detailed descriptions for each code.",
  },
  {
    question: "How do I appeal a denied claim?",
    answer: "Go to the Denials page, find the denied claim, and click the appeal icon. Fill in the justification form and submit. Appeals are tracked separately and you'll receive notifications on status changes.",
  },
  {
    question: "What is the A/R Aging report?",
    answer: "The A/R Aging report shows all outstanding claims organized by how long they've been unpaid. Claims are grouped into buckets: 0-30, 31-60, 61-90, 91-120, and 120+ days.",
  },
  {
    question: "How do I export reports?",
    answer: "Each page has an 'Export' button that downloads data as a CSV file. You can also access the Reports page for more comprehensive analytics and custom date ranges.",
  },
  {
    question: "How are billing amounts calculated?",
    answer: "Billing amounts are calculated based on the CPT codes assigned to each session. Each CPT code has a standard fee schedule. You can view and modify these in Settings.",
  },
  {
    question: "What does 'Timely Filing' mean?",
    answer: "Timely filing refers to the deadline by which claims must be submitted to payers. This varies by payer but is typically 90-365 days from the date of service. Late submissions may be denied.",
  },
  {
    question: "How do I add a new payer?",
    answer: "Navigate to the Payers page and click 'Add Payer'. Enter the payer information including name, payer ID, and contact details. You can also set payer-specific timely filing limits.",
  },
];

const resources = [
  {
    title: "Billing Quick Start Guide",
    description: "Learn the basics of using the RCM portal",
    icon: Book,
    type: "PDF",
  },
  {
    title: "CPT Code Reference",
    description: "Complete list of supported procedure codes",
    icon: FileText,
    type: "PDF",
  },
  {
    title: "Denial Management Training",
    description: "Video walkthrough of handling denials",
    icon: Video,
    type: "Video",
  },
  {
    title: "Payer Requirements Guide",
    description: "Submission requirements by insurance type",
    icon: FileText,
    type: "PDF",
  },
];

export default function BillingHelp() {
  const [searchTerm, setSearchTerm] = useState("");
  const [supportSubject, setSupportSubject] = useState("");
  const [supportMessage, setSupportMessage] = useState("");

  const filteredFaqs = faqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmitTicket = () => {
    if (!supportSubject.trim() || !supportMessage.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    toast.success("Support ticket submitted successfully");
    setSupportSubject("");
    setSupportMessage("");
  };

  return (
    <div className="flex min-h-screen bg-background">
      <BillingSidebar />
      <div className="flex-1 flex flex-col">
        <BillingHeader />
        <main className="flex-1 p-6 space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Help Center</h1>
            <p className="text-muted-foreground">Find answers and get support</p>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-primary/10">
                    <MessageCircle className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Live Chat</h3>
                    <p className="text-sm text-muted-foreground">Chat with support</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-success/10">
                    <Phone className="h-6 w-6 text-success" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Call Support</h3>
                    <p className="text-sm text-muted-foreground">1-800-VIVIEN</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-warning/10">
                    <Mail className="h-6 w-6 text-warning" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Email Support</h3>
                    <p className="text-sm text-muted-foreground">billing@vivien.health</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* FAQs */}
            <div className="lg:col-span-2 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HelpCircle className="h-5 w-5" />
                    Frequently Asked Questions
                  </CardTitle>
                  <CardDescription>
                    <div className="relative mt-2">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search FAQs..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="w-full">
                    {filteredFaqs.map((faq, index) => (
                      <AccordionItem key={index} value={`item-${index}`}>
                        <AccordionTrigger className="text-left">
                          {faq.question}
                        </AccordionTrigger>
                        <AccordionContent className="text-muted-foreground">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                  {filteredFaqs.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">
                      No matching FAQs found
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Resources */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Book className="h-5 w-5" />
                    Resources & Training
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {resources.map((resource, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3 p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                      >
                        <div className="p-2 rounded-lg bg-muted">
                          <resource.icon className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{resource.title}</h4>
                            <ExternalLink className="h-3 w-3 text-muted-foreground" />
                          </div>
                          <p className="text-sm text-muted-foreground">{resource.description}</p>
                          <span className="text-xs text-primary">{resource.type}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Support Ticket */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Submit a Ticket</CardTitle>
                  <CardDescription>Can't find what you're looking for?</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject</Label>
                    <Input
                      id="subject"
                      placeholder="Brief description of your issue"
                      value={supportSubject}
                      onChange={(e) => setSupportSubject(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      placeholder="Describe your issue in detail..."
                      rows={5}
                      value={supportMessage}
                      onChange={(e) => setSupportMessage(e.target.value)}
                    />
                  </div>
                  <Button onClick={handleSubmitTicket} className="w-full">
                    Submit Ticket
                  </Button>
                </CardContent>
              </Card>

              {/* System Status */}
              <Card>
                <CardHeader>
                  <CardTitle>System Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    { name: "Claims Processing", status: "operational" },
                    { name: "Payer Connections", status: "operational" },
                    { name: "Report Generation", status: "operational" },
                    { name: "Real-time Updates", status: "operational" },
                  ].map((service) => (
                    <div key={service.name} className="flex items-center justify-between">
                      <span className="text-sm">{service.name}</span>
                      <div className="flex items-center gap-1 text-success">
                        <CheckCircle className="h-4 w-4" />
                        <span className="text-xs capitalize">{service.status}</span>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
